import { pageTitle } from '@palaccount/config';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { api } from '@/api';
import { BrandMark } from '@/components/brand-mark';
import { LanguageSwitch } from '@/components/language-switch';

/**
 * BR-GEN-S2-08/09 — the activation link from the signup email.
 *
 * There is nothing to fill in, so there is no form: the page confirms the
 * address on arrival and then says what happened. The one piece of state
 * worth having is the guard against React's development double-invocation of
 * effects, because the token is single-use and firing it twice would show a
 * "no longer valid" message to somebody whose link had just worked.
 */
export function VerifyEmailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const fired = useRef(false);

  const [state, setState] = useState<'working' | 'ok' | 'bad'>(token === '' ? 'bad' : 'working');

  useEffect(() => {
    document.title = pageTitle(t('verify.title'));
  }, [t]);

  useEffect(() => {
    if (token === '' || fired.current) return;
    fired.current = true;
    void (async () => {
      try {
        const response = await api.auth.verifyEmail({ body: { token } });
        setState(response.status === 200 ? 'ok' : 'bad');
      } catch {
        setState('bad');
      }
    })();
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <div className="w-full max-w-md rounded-xl border bg-background p-8 shadow-sm">
        <div className="mb-6 flex items-center justify-between">
          <BrandMark />
          <LanguageSwitch />
        </div>
        <div className="grid gap-4">
          <h1 className="text-xl font-bold">{t('verify.title')}</h1>
          {state === 'working' ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 aria-hidden className="animate-spin" />
              {t('verify.working')}
            </p>
          ) : state === 'ok' ? (
            <p className="text-sm text-muted-foreground">{t('verify.ok')}</p>
          ) : (
            <p role="alert" className="text-sm text-destructive">
              {t('verify.bad')}
            </p>
          )}
          <Link className="text-sm text-primary underline" to="/sign-in">
            {t('auth.signIn')}
          </Link>
        </div>
      </div>
    </div>
  );
}
